import { Component, Input, OnChanges } from '@angular/core';
import { ChartTypeOption } from '../chart-type-toggle/chart-type-toggle.component';
import { ReportColumn } from '../data-table/data-table.component';
import { THEME_COLORS } from '../../utils/chart-theme';

const SEGMENT_COLORS: any = {
  success: THEME_COLORS.success,
  danger: THEME_COLORS.danger,
  warning: THEME_COLORS.warning,
  info: THEME_COLORS.info,
  primary: THEME_COLORS.primary
};

const STACKED_TYPES: ChartTypeOption[] = [
  { id: 'rows', label: 'Bars' },
  { id: 'stacked', label: 'Stacked' },
  { id: 'table', label: 'Table' }
];

const GRID_STROKE = '#dee2e6';
const TICK_MUTED = '#98a6ad';
const TICK_BODY = '#323a46';

function segmentTotal(item: any): number {
  return (item.segments || []).reduce((sum: number, segment: any) => sum + (Number(segment.value) || 0), 0);
}

@Component({
  selector: 'app-br-stacked-bar-chart',
  templateUrl: './stacked-bar-chart.component.html',
  host: { '[attr.title]': 'null' }
})
export class BrStackedBarChartComponent implements OnChanges {
  @Input() chartTitle: string;
  /** Short line under the title explaining how the chart values are calculated. */
  @Input() chartDescription: string;
  @Input() items: any[] = [];
  @Input() maxItems = 8;
  @Input() emptyLabel = 'No data';

  readonly chartTypeOptions = STACKED_TYPES;
  chartType = 'table';

  visibleItems: any[] = [];
  maxTotal = 1;
  legendSegments: any[] = [];

  chartLabels: string[] = [];
  chartDatasets: any[] = [];
  chartOptions: any = {};
  tableColumns: ReportColumn[] = [];
  tableRows: any[] = [];

  ngOnChanges() {
    this.visibleItems = [...(this.items || [])]
      .sort((a, b) => segmentTotal(b) - segmentTotal(a))
      .slice(0, this.maxItems);

    this.maxTotal = Math.max(...this.visibleItems.map(segmentTotal), 1);

    const seen = new Map<string, any>();
    this.visibleItems
      .reduce((acc: any[], item) => acc.concat(item.segments || []), [])
      .filter((segment: any) => Number(segment.value) > 0)
      .forEach((segment: any) => seen.set(segment.label, segment));
    this.legendSegments = [...seen.values()];

    this.buildTableData();
    this.rebuildChart();
  }

  onChartTypeChange(type: string) {
    this.chartType = type;
  }

  rowWidthPct(item: any): number {
    const total = segmentTotal(item);
    const pct = Math.round((total / this.maxTotal) * 100);
    return Math.max(pct, total > 0 ? 8 : 0);
  }

  segmentWidthPct(item: any, segment: any): number {
    const total = segmentTotal(item);
    const value = Number(segment.value) || 0;
    return total ? (value / total) * 100 : 0;
  }

  rowTotal(item: any): number {
    return segmentTotal(item);
  }

  segmentColor(colorClass: string): string {
    return SEGMENT_COLORS[colorClass] || SEGMENT_COLORS.primary;
  }

  private segmentKey(label: string): string {
    return 'seg_' + label.replace(/\W+/g, '_');
  }

  private buildTableData() {
    const segmentLabels = this.legendSegments.map((segment) => segment.label);

    this.tableColumns = [
      { key: 'label', header: 'Name' },
      ...segmentLabels.map((segLabel) => ({
        key: this.segmentKey(segLabel),
        header: segLabel,
        value: true,
        render: (row: any) => String(row[this.segmentKey(segLabel)] != null ? row[this.segmentKey(segLabel)] : 0)
      })),
      { key: 'total', header: 'Total', value: true }
    ];

    this.tableRows = this.visibleItems.map((item) => {
      const row: any = { label: item.label, total: this.rowTotal(item) };
      segmentLabels.forEach((segLabel) => {
        const seg = (item.segments || []).find((s: any) => s.label === segLabel);
        row[this.segmentKey(segLabel)] = seg ? Number(seg.value) || 0 : 0;
      });
      return row;
    });
  }

  private rebuildChart() {
    const allLabels = this.visibleItems.reduce(
      (acc: string[], item) => acc.concat((item.segments || []).map((s: any) => s.label)),
      []
    );
    const segmentLabels = [...new Set(allLabels)] as string[];

    this.chartLabels = this.visibleItems.map((item) => item.label);
    this.chartDatasets = segmentLabels.map((label) => {
      const matchedSegment = this.legendSegments.find((s) => s.label === label);
      const colorClass = matchedSegment ? matchedSegment.colorClass : undefined;
      return {
        label,
        data: this.visibleItems.map((item) => {
          const seg = (item.segments || []).find((s: any) => s.label === label);
          return seg ? Number(seg.value) || 0 : 0;
        }),
        backgroundColor: this.segmentColor(colorClass)
      };
    });

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      tooltips: {
        callbacks: {
          title: () => null,
          label: (tooltipItem: any, data: any) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            return `${dataset.label}: ${tooltipItem.xLabel}`;
          }
        }
      },
      scales: {
        xAxes: [{ stacked: true, gridLines: { color: GRID_STROKE, drawBorder: false }, ticks: { fontColor: TICK_MUTED, fontSize: 12, beginAtZero: true } }],
        yAxes: [{ stacked: true, gridLines: { display: false, drawBorder: false }, ticks: { fontColor: TICK_BODY, fontSize: 12 } }]
      }
    };
  }

  get chartHeight(): number {
    return Math.max(260, this.visibleItems.length * 36 + 48);
  }
}
